// ============================================================
// v11 §2G (BOLT-027) — THE cut geometry, extracted. One source of
// truth from engine to tutorial: the server's resolvers (engine.ts),
// the clients' live cut previews, and the codex demo animations all
// consume THESE builders, so a preview or demo can never drift from
// the real cut (§2K). Everything here is pure geometry over PUBLIC
// inputs — a seeker pin, card params, the boundary — never `H`.
// ============================================================
import { bearing as turfBearing, buffer, circle, destination, difference, featureCollection, area, point, polygon } from '@turf/turf';
import type { Feature, MultiPolygon, Polygon, Position } from 'geojson';
import type { Boundary, LngLat } from './protocol';

export type Cardinal = 'N' | 'E' | 'S' | 'W';
/** v11 §2K: the Quadrants card's four corners (axis-aligned cross through S). */
export type Corner = 'NW' | 'NE' | 'SE' | 'SW';

const HUGE_M = 8000; // covers any neighbourhood-scale zone; treat builders as ~infinite
const HUGE_DEG = 0.1; // ~11 km — the axis-aligned quadrant reach

const pt = (p: LngLat) => point([p.lng, p.lat]);

// ---------------- radar ----------------
/** The radar disk around the seeker — the resolver keeps/removes it; the
 *  preview shows its two candidate sections (in/out). */
export function radarDisk(S: LngLat, radiusM: number): Feature<Polygon> {
  return circle(pt(S), radiusM, { units: 'meters', steps: 96 }) as Feature<Polygon>;
}

// ---------------- direction (X-pattern wedges) ----------------
export const WEDGE_START: Record<Cardinal, number> = { N: 315, E: 45, S: 135, W: 225 };

/** A 90° wedge from S for the given cardinal, as a finite (but huge) polygon.
 *  NB (v11 §2K): Direction cuts THESE diagonal-edged wedges (an X through S),
 *  not axis-aligned quadrants — the codex demo renders from this same builder. */
export function directionWedge(S: LngLat, dir: Cardinal, steps = 48): Feature<Polygon> {
  const start = WEDGE_START[dir];
  const ring: Position[] = [[S.lng, S.lat]];
  for (let i = 0; i <= steps; i++) {
    const brng = start + (90 * i) / steps;
    const d = destination(pt(S), HUGE_M, brng, { units: 'meters' });
    ring.push(d.geometry.coordinates as Position);
  }
  ring.push([S.lng, S.lat]);
  return polygon([ring]);
}

/** All four wedges in stable N/E/S/W order (the preview's candidate sections). */
export function directionWedges(S: LngLat): { dir: Cardinal; region: Feature<Polygon> }[] {
  return (['N', 'E', 'S', 'W'] as Cardinal[]).map((dir) => ({ dir, region: directionWedge(S, dir) }));
}

// ---------------- quadrants (axis-aligned cross — v11 §2K) ----------------
/** One axis-aligned quadrant rectangle from S (the cross-pattern cut). */
export function quadrantRegion(S: LngLat, corner: Corner): Feature<Polygon> {
  const north = corner === 'NW' || corner === 'NE';
  const east = corner === 'NE' || corner === 'SE';
  const lat2 = S.lat + (north ? HUGE_DEG : -HUGE_DEG);
  const lng2 = S.lng + (east ? HUGE_DEG : -HUGE_DEG);
  return polygon([
    [
      [S.lng, S.lat],
      [lng2, S.lat],
      [lng2, lat2],
      [S.lng, lat2],
      [S.lng, S.lat],
    ],
  ]);
}

/** All four quadrants in stable NW/NE/SE/SW order. */
export function quadrantRegions(S: LngLat): { corner: Corner; region: Feature<Polygon> }[] {
  return (['NW', 'NE', 'SE', 'SW'] as Corner[]).map((corner) => ({ corner, region: quadrantRegion(S, corner) }));
}

/** Which quadrant H falls in — boundary-inclusive toward N and E (parity with
 *  the half-open wedge rule: an exact tie goes to the higher side). */
export function cornerOf(S: LngLat, H: LngLat): Corner {
  const north = H.lat >= S.lat;
  const east = H.lng >= S.lng;
  return north ? (east ? 'NE' : 'NW') : east ? 'SE' : 'SW';
}

// ---------------- perpendicular bisector (community-card scripting API) ----------------
/** The half-plane of points nearer `near` than `far` — the `near` side of the
 *  perpendicular bisector of the segment — as a huge rectangle. This is the
 *  FROZEN `geo.halfPlaneBisector` sandbox builder (community cards may rely on
 *  it); it is NOT what the Slice card cuts (see sliceHalfPlane below). */
export function bisectorHalfPlane(near: LngLat, far: LngLat): Feature<Polygon> {
  const mid: LngLat = { lat: (near.lat + far.lat) / 2, lng: (near.lng + far.lng) / 2 };
  const toNear = turfBearing(pt(far), pt(near)); // perpendicular-bisector normal points to `near`
  const along1 = toNear + 90;
  const along2 = toNear - 90;
  const p1 = destination(pt(mid), HUGE_M, along1, { units: 'meters' });
  const p2 = destination(pt(mid), HUGE_M, along2, { units: 'meters' });
  const p3 = destination(p2, HUGE_M, toNear, { units: 'meters' });
  const p4 = destination(p1, HUGE_M, toNear, { units: 'meters' });
  return polygon([
    [
      p1.geometry.coordinates as Position,
      p2.geometry.coordinates as Position,
      p3.geometry.coordinates as Position,
      p4.geometry.coordinates as Position,
      p1.geometry.coordinates as Position,
    ],
  ]);
}

// ---------------- slice (cut ALONG the walk line — v21 BOLT-101) ----------------
/** The side of the walk, clockwise from the direction of travel. */
export type SliceSide = 'L' | 'R';

/** The half-plane on `side` of the INFINITE line through A and B, as a huge
 *  rectangle. Walking A→B, 'R' is the seeker's right, 'L' their left. Unlike the
 *  old thermometer (which cut the PERPENDICULAR bisector of the two points), Slice
 *  cuts ALONG the A→B line itself — the map is split by the path you just walked.
 *  The live preview feeds (P_start, P_current) and gets the EXACT eventual cut. */
export function sliceHalfPlane(A: LngLat, B: LngLat, side: SliceSide): Feature<Polygon> {
  const mid: LngLat = { lat: (A.lat + B.lat) / 2, lng: (A.lng + B.lng) / 2 };
  const walk = turfBearing(pt(A), pt(B)); // the A→B line direction
  const toSide = walk + (side === 'R' ? 90 : -90); // normal toward the kept side
  const p1 = destination(pt(mid), HUGE_M, walk, { units: 'meters' }); // far ahead on the line
  const p2 = destination(pt(mid), HUGE_M, walk + 180, { units: 'meters' }); // far behind on the line
  const p3 = destination(p2, HUGE_M, toSide, { units: 'meters' });
  const p4 = destination(p1, HUGE_M, toSide, { units: 'meters' });
  return polygon([
    [
      p1.geometry.coordinates as Position,
      p2.geometry.coordinates as Position,
      p3.geometry.coordinates as Position,
      p4.geometry.coordinates as Position,
      p1.geometry.coordinates as Position,
    ],
  ]);
}

/** Both sides of the walk line in stable [right, left] order (the preview's
 *  candidate sections for a walk from Pstart to Pcur). */
export function sliceHalfPlanes(Pstart: LngLat, Pcur: LngLat): Feature<Polygon>[] {
  return [sliceHalfPlane(Pstart, Pcur, 'R'), sliceHalfPlane(Pstart, Pcur, 'L')];
}

// ---------------- borders (outer band vs inner core — v11 §2L) ----------------
export interface BordersRegions {
  core: Feature<Polygon | MultiPolygon>; // the inner 75% of the boundary's area
  band: Feature<Polygon | MultiPolygon>; // the outer 25% ring
  erosionM: number; // the solved inset distance (diagnostics/tests)
}

/** The Borders card's two regions: `core` = a negative-buffer inset of the
 *  boundary holding 75% of its area (erosion distance solved by bisection),
 *  `band` = boundary ∖ core. A concave boundary may yield a MultiPolygon
 *  core — fine. Deterministic given the boundary ⇒ unit-testable tight. */
export function bordersRegions(boundary: Boundary, coreShare = 0.75): BordersRegions | null {
  const total = area(boundary);
  if (!(total > 0)) return null;
  const target = total * coreShare;
  let lo = 0; // erosion (m) — area(inset) ≥ target here
  let hi = Math.sqrt(total); // safely empties any shape of this area
  let core: Feature<Polygon | MultiPolygon> | null = null;
  let coreAt = 0;
  for (let i = 0; i < 48; i++) {
    const mid = (lo + hi) / 2;
    let inset: Feature<Polygon | MultiPolygon> | undefined;
    try {
      inset = buffer(boundary, -mid / 1000, { units: 'kilometers' }) as Feature<Polygon | MultiPolygon> | undefined;
    } catch {
      inset = undefined;
    }
    const a = inset ? area(inset) : 0;
    if (a >= target) {
      lo = mid;
      core = inset ?? core;
      coreAt = mid;
    } else {
      hi = mid;
    }
  }
  if (!core) return null;
  const band = difference(featureCollection([boundary as Feature<Polygon | MultiPolygon>, core]));
  if (!band) return null;
  return { core, band: band as Feature<Polygon | MultiPolygon>, erosionM: coreAt };
}

// ---------------- v13 BOLT-037: preview-line clipping ----------------
// The preview's dashed LINES (radials / bisector / rings) must never span
// outside the play area — Jake: previews "should only span within the
// boundaries of the play area". Deterministic planar clip (no turf) so the
// Swift port (GeoCut.clipLine) can match it arithmetic-for-arithmetic;
// pinned by the gen:swift cut-geometry.json fixtures.

/** Even-odd ray cast; ring is a closed GeoJSON ring ([lng,lat], first==last). */
export function pointInRing(p: Position, ring: Position[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if (yi > p[1] !== yj > p[1] && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** Proper-crossing parameter t along p→q against segment a→b, else null. */
const segT = (p: Position, q: Position, a: Position, b: Position): number | null => {
  const d1x = q[0] - p[0];
  const d1y = q[1] - p[1];
  const d2x = b[0] - a[0];
  const d2y = b[1] - a[1];
  const denom = d1x * d2y - d1y * d2x;
  if (Math.abs(denom) < 1e-15) return null;
  const t = ((a[0] - p[0]) * d2y - (a[1] - p[1]) * d2x) / denom;
  const u = ((a[0] - p[0]) * d1y - (a[1] - p[1]) * d1x) / denom;
  return t > 1e-9 && t < 1 - 1e-9 && u >= -1e-9 && u <= 1 + 1e-9 ? t : null;
};

/** Clip a polyline to the boundary polygon: split every segment at ring
 *  crossings and keep the sub-segments whose midpoints fall inside, joined
 *  back into maximal runs. Fully inside → [line]; fully outside → []. */
export function clipLineToBoundary(line: Position[], boundary: Boundary): Position[][] {
  const ring = boundary.geometry.coordinates[0] as Position[];
  const out: Position[][] = [];
  let current: Position[] = [];
  const flush = () => {
    if (current.length >= 2) out.push(current);
    current = [];
  };
  for (let i = 0; i < line.length - 1; i++) {
    const p = line[i];
    const q = line[i + 1];
    const ts: number[] = [0, 1];
    for (let j = 0; j < ring.length - 1; j++) {
      const t = segT(p, q, ring[j], ring[j + 1]);
      if (t !== null) ts.push(t);
    }
    ts.sort((x, y) => x - y);
    for (let k = 0; k < ts.length - 1; k++) {
      const t0 = ts[k];
      const t1 = ts[k + 1];
      if (t1 - t0 < 1e-12) continue;
      const at = (t: number): Position => [p[0] + t * (q[0] - p[0]), p[1] + t * (q[1] - p[1])];
      if (pointInRing(at((t0 + t1) / 2), ring)) {
        if (current.length === 0) current.push(at(t0));
        current.push(at(t1));
      } else {
        flush();
      }
    }
  }
  flush();
  return out;
}
